import type { DateString } from '@seen/core/dates'
import { type JSX, type ReactNode, useState } from 'react'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'

// The picker's typed field reports every keystroke as a date, so a pick waits
// for typing to settle and for a four-digit year: "1950" passes through
// 0001, 0019 and 0195 on the way. Closing the picker settles at once.
const SETTLE_MS = 600

function isWholeDate(value: DateString): boolean {
  return Number(value.slice(0, 4)) >= 1000
}

export interface ObservationDatePickerProps {
  // The day already recorded, if any.
  value: DateString | null
  onPick: (value: DateString) => void
  // The trigger: the unanswered chip, or the recorded value.
  children: ReactNode
}

// Picks the day for a date or timestamp observation node.
export function ObservationDatePicker({
  value,
  onPick,
  children,
}: ObservationDatePickerProps): JSX.Element {
  // The node stays engaged while its calendar is open, so its actions stay
  // in reach.
  const reportOpen = useReportPopoverOpen(false)
  const [picked, setPicked] = useState<DateString | null>(value)
  const settle = useDebouncedCallback((day: DateString) => {
    if (day !== value) {
      onPick(day)
    }
  }, SETTLE_MS)

  return (
    <DatePicker
      asChild
      allowUndefined
      clearText={null}
      value={picked}
      // Non-modal: a click on the node's actions reaches them and closes
      // the calendar, rather than landing on a layer over the page.
      modal={false}
      onOpenChange={(open) => {
        if (!open) {
          settle.flush()
        }
        reportOpen(open)
      }}
      onChange={(day: DateString | null) => {
        setPicked(day)
        if (day && isWholeDate(day)) {
          settle(day)
        } else {
          settle.cancel()
        }
      }}
    >
      {children}
    </DatePicker>
  )
}
